import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { authClient } from "@/lib/auth-client";
import { userKeys } from "@/lib/query";

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  createdAt: Date;
}

export type { User };

// Better Auth's admin plugin types `role` as the "user" | "admin" union. The
// role values that reach these hooks originate from a fixed two-option form, so
// anything that isn't "admin" is "user" — narrow the loose string here at the
// boundary instead of casting.
function toUserRole(role: string): "user" | "admin" {
  return role === "admin" ? "admin" : "user";
}

export function useUsers(filters: { search?: string }) {
  return useQuery({
    queryKey: userKeys.list(filters),
    queryFn: async (): Promise<User[]> => {
      const { data, error } = await authClient.admin.listUsers({
        query: {
          limit: 100,
          ...(filters.search
            ? {
                searchField: "email" as const,
                searchValue: filters.search,
                searchOperator: "contains" as const,
              }
            : {}),
        },
      });
      if (error) throw error;
      // Map Better Auth's user shape into our local `User` at the boundary
      // instead of casting — `role` is narrowed via `toUserRole`.
      return (data?.users ?? []).map((u) => ({
        id: u.id,
        name: u.name,
        email: u.email,
        role: toUserRole(u.role ?? "user"),
        createdAt: u.createdAt,
      }));
    },
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; email: string; password: string; role: string }) => {
      const { error } = await authClient.admin.createUser({
        ...input,
        role: toUserRole(input.role),
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.success("User created");
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "Failed to create user");
    },
  });
}

export function useChangeRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: string; role: string }) => {
      const { error } = await authClient.admin.setRole({
        userId: input.userId,
        role: toUserRole(input.role),
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.success("Role updated");
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "Failed to change role");
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await authClient.admin.removeUser({ userId });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.success("User deleted");
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "Failed to delete user");
    },
  });
}

export function useResetPassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: string; newPassword: string }) => {
      const { error } = await authClient.admin.setUserPassword(input);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.success("Password reset");
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "Failed to reset password");
    },
  });
}
